import { Button, DatePicker, Form, Input, Popconfirm, Select } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { fuelImportApi } from '../../../../../apis'
import {
  ActionEnum,
  AlertMessageEnum,
  getDateFormat,
} from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { FuelImportEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadStations,
  loadSuppliers,
  loadTanks,
  setAction,
  setActionProcessing,
  setAlert,
  setFuelImportFilter,
  setFuelImports,
  setSelectedFuelImport,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputFuelImport = {
  name: string
  importDate: Date
  volume: number
  unitPrice: number
  amountPaid: number
  vatPercent: number
  supplierId: number
  tankId: number
  fuelId: number
  note?: string
}

export const ManageFuelImportModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { fuelImports, fuelImportFilter } = useSelector(
    (state: RootState) => state.fuelImport
  )
  const { stations, isStationsLoaded } = useSelector(
    (state: RootState) => state.station
  )
  const { tanks, isTanksLoaded } = useSelector((state: RootState) => state.tank)
  const { suppliers, isSuppliersLoaded } = useSelector(
    (state: RootState) => state.supplier
  )
  const { language } = useSelector((state: RootState) => state.language)
  const { selectedFuelImport } = useSelector(
    (state: RootState) => state.fuelImport
  )
  const { management } = useSelector((state: RootState) => state.common)

  const [isPaying, setIsPaying] = React.useState(false)

  React.useEffect(() => {
    dispatch(loadStations())
    dispatch(loadTanks())
    dispatch(loadSuppliers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const [total, setTotal] = React.useState(
    selectedFuelImport
      ? selectedFuelImport.volume * selectedFuelImport.unitPrice
      : 0
  )

  const [selectedOwner, setSelectedOwner] = React.useState(
    selectedFuelImport ? selectedFuelImport.tank.station.owner.name : undefined
  )
  const [selectedFuel, setSelectedFuel] = React.useState(
    selectedFuelImport ? selectedFuelImport.fuel.name : undefined
  )

  const calTotal = () => {
    if (form.getFieldValue('unitPrice') && form.getFieldValue('volume')) {
      const vatPercent = form.getFieldValue('vatPercent') || 0
      setTotal(
        form.getFieldValue('unitPrice') *
          form.getFieldValue('volume') *
          (1 + vatPercent / 100)
      )
    } else {
      setTotal(0)
    }
  }

  const replaceFuelImport = (fuelImport: FuelImportEntity) => {
    const index = fuelImports.map((item) => item.id).indexOf(fuelImport.id)
    if (index > -1) {
      dispatch(
        setFuelImports([
          ...fuelImports.slice(0, index),
          fuelImport,
          ...fuelImports.slice(index + 1),
        ])
      )
    }
  }

  const pay = async () => {
    if (selectedFuelImport) {
      const accountsPayable =
        selectedFuelImport.unitPrice * selectedFuelImport.volume -
        selectedFuelImport.amountPaid
      try {
        setIsPaying(true)
        dispatch(setActionProcessing(true))
        await fuelImportApi.updateFuelImport(selectedFuelImport.id, {
          accountsPayable,
          reason: `${translate('fuelImport.pay.reason', language)}${
            selectedFuelImport.id
          })`,
        })
        dispatch(
          setFuelImportFilter({
            ...fuelImportFilter,
            importDateTo: new Date().getTime(),
          })
        )
        replaceFuelImport({
          ...selectedFuelImport,
          amountPaid: selectedFuelImport.amountPaid + accountsPayable,
        })
        dispatch(
          setAlert({
            key: 'fuelImport.pay.success',
            type: AlertMessageEnum.SUCCESS,
          })
        )
      } catch (e) {
        if (e.response && e.response.status && e.response.status === 401) {
          await fuelImportApi.updateFuelImport(selectedFuelImport.id, {
            accountsPayable,
            reason: `${translate('fuelImport.pay.reason', language)}${
              selectedFuelImport.id
            })`,
          })
          dispatch(
            setFuelImportFilter({
              ...fuelImportFilter,
              importDateTo: new Date().getTime(),
            })
          )
          replaceFuelImport({
            ...selectedFuelImport,
            amountPaid: selectedFuelImport.amountPaid + accountsPayable,
          })
          dispatch(
            setAlert({
              key: 'fuelImport.pay.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } else {
          dispatch(
            setAlert({
              key: 'fuelImport.pay.fail',
              type: AlertMessageEnum.ERROR,
            })
          )
        }
      } finally {
        dispatch(setActionProcessing(false))
        setIsPaying(false)
      }
    }
  }

  const onFinish = async (value: InputFuelImport) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedFuelImport) {
          try {
            const respUpdate = await fuelImportApi.updateFuelImport(
              selectedFuelImport.id,
              {
                ...value,
                importDate: new Date(
                  value.importDate || selectedFuelImport.importDate
                ).getTime(),
              }
            )
            dispatch(
              setFuelImportFilter({
                ...fuelImportFilter,
                importDateTo: new Date().getTime(),
              })
            )
            replaceFuelImport(respUpdate.data)
            dispatch(setSelectedFuelImport())
            dispatch(
              setAlert({
                key: 'fuelImport.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              const respUpdate = await fuelImportApi.updateFuelImport(
                selectedFuelImport.id,
                {
                  ...value,
                  importDate: new Date(
                    value.importDate || selectedFuelImport.importDate
                  ).getTime(),
                }
              )
              dispatch(
                setFuelImportFilter({
                  ...fuelImportFilter,
                  importDateTo: new Date().getTime(),
                })
              )
              replaceFuelImport(respUpdate.data)
              dispatch(setSelectedFuelImport())
              dispatch(
                setAlert({
                  key: 'fuelImport.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'fuelImport.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          const respCreate = await fuelImportApi.createFuelImport({
            ...value,
            importDate: new Date(value.importDate).getTime(),
            vatPercent: value.vatPercent || 0,
            amountPaid: value.amountPaid || 0,
            reason:
              value.amountPaid > 0
                ? `${translate('fuelImport.pay.reason', language)}`
                : undefined,
          })
          dispatch(
            setFuelImportFilter({
              ...fuelImportFilter,
              importDateTo: new Date().getTime(),
            })
          )
          dispatch(setFuelImports([respCreate.data, ...fuelImports]))
          dispatch(setSelectedFuelImport())
          dispatch(
            setAlert({
              key: 'fuelImport.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 401) {
            const respCreate = await fuelImportApi.createFuelImport({
              ...value,
              importDate: new Date(value.importDate).getTime(),
              vatPercent: value.vatPercent || 0,
              amountPaid: value.amountPaid || 0,
              reason:
                value.amountPaid > 0
                  ? `${translate('pay.reason', language)}`
                  : undefined,
            })
            dispatch(
              setFuelImportFilter({
                ...fuelImportFilter,
                importDateTo: new Date().getTime(),
              })
            )
            dispatch(setFuelImports([respCreate.data, ...fuelImports]))
            dispatch(setSelectedFuelImport())
            dispatch(
              setAlert({
                key: 'fuelImport.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'fuelImport.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        if (selectedFuelImport) {
          try {
            await fuelImportApi.deleteFuelImport(selectedFuelImport.id)
            dispatch(
              setFuelImports(
                fuelImports.filter((item) => item.id !== selectedFuelImport.id)
              )
            )
            dispatch(setSelectedFuelImport())
            dispatch(
              setAlert({
                key: 'fuelImport.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              await fuelImportApi.deleteFuelImport(selectedFuelImport.id)
              dispatch(
                setFuelImports(
                  fuelImports.filter(
                    (item) => item.id !== selectedFuelImport.id
                  )
                )
              )
              dispatch(setSelectedFuelImport())
              dispatch(
                setAlert({
                  key: 'fuelImport.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'fuelImport.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <>
      <Form onFinish={onFinish} form={form}>
        {(!management.alertMessage ||
          management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
          <>
            {readOnlyMode && (
              <>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.name', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.importDate', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.importDate
                    ? getDateFormat(selectedFuelImport.importDate)
                    : ''}
                </StyledFormItem>
              </>
            )}
            {!readOnlyMode && (
              <>
                <StyledFormItem
                  name="importDate"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.importDate', language)}
                    </StyledLabel>
                  }
                  initialValue={moment(
                    selectedFuelImport?.importDate || new Date()
                  )}
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || moment(value).toDate() > new Date()) {
                          return Promise.reject(
                            new Error(
                              translate(
                                'fuelImport.invalid.importDate',
                                language
                              )
                            )
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder={translate('fuelImport.importDate', language)}
                    format="YYYY-MM-DD"
                  />
                </StyledFormItem>
                <StyledFormItem
                  name="name"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.name', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.name}
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || value.trim().length < 1) {
                          return Promise.reject(
                            new Error(
                              translate('fuelImport.invalid.name', language)
                            )
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder={translate('fuelImport.name', language)}
                    allowClear
                  />
                </StyledFormItem>
              </>
            )}
            {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
              <>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.supplier', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.supplier.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.fuel', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.fuel.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.station', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.tank.station.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.owner', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.tank.station.owner.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.tank', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.tank.name}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.creator', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.creator.name}
                </StyledFormItem>
              </>
            )}
            {readOnlyMode && (
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('fuelImport.note', language)}
                  </StyledLabel>
                }
              >
                {selectedFuelImport?.note}
              </StyledFormItem>
            )}
            {management.action === ActionEnum.CREATE && (
              <>
                <StyledFormItem
                  name="stationId"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.station', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.tank.station.id}
                  rules={[
                    {
                      required: true,
                      message: translate(
                        'fuelImport.required.station',
                        language
                      ),
                    },
                  ]}
                >
                  <Select
                    placeholder={translate('fuelImport.station', language)}
                    style={{ width: '100%' }}
                    loading={!isStationsLoaded}
                    disabled={!isStationsLoaded}
                    onChange={(e) => {
                      form.setFieldsValue({ tankId: undefined })
                      setSelectedOwner(
                        stations
                          .filter((item) => item.id === e)
                          .map((item) => item.owner.name)
                          .join('')
                      )
                    }}
                  >
                    {stations.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {`${item.name} ( ${item.address} )`}
                      </Select.Option>
                    ))}
                  </Select>
                </StyledFormItem>
                {selectedOwner && (
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('fuelImport.owner', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedOwner}
                  </StyledFormItem>
                )}
                <StyledFormItem
                  name="tankId"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.tank', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.tank.id}
                  rules={[
                    {
                      required: true,
                      message: translate('fuelImport.required.tank', language),
                    },
                  ]}
                >
                  <Select
                    placeholder={translate('fuelImport.tank', language)}
                    style={{ width: '100%' }}
                    loading={!isStationsLoaded || !isTanksLoaded}
                    disabled={
                      !isStationsLoaded ||
                      !isTanksLoaded ||
                      !form.getFieldValue('stationId')
                    }
                    onChange={(e: any) => {
                      const index = tanks
                        .map((item) => item.id)
                        .indexOf(parseInt(e))
                      if (index > -1) {
                        const tank = tanks[index]
                        form.setFieldsValue({
                          fuelId: tank.fuel ? tank.fuel.id : undefined,
                        })
                        setSelectedFuel(tank.fuel ? tank.fuel.name : '')
                      } else {
                        form.setFieldsValue({ fuelId: undefined })
                        setSelectedFuel('')
                      }
                    }}
                  >
                    {tanks
                      .filter(
                        (item) =>
                          item.station.id === form.getFieldValue('stationId')
                      )
                      .map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {`${item.name} (${item.fuel.name})`}
                        </Select.Option>
                      ))}
                  </Select>
                </StyledFormItem>
                {selectedFuel && (
                  <StyledFormItem
                    name="fuelId"
                    label={
                      <StyledLabel>
                        {translate('fuelImport.fuel', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedFuel}
                  </StyledFormItem>
                )}
                <StyledFormItem
                  name="supplierId"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.supplier', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.supplier.id}
                  rules={[
                    {
                      required: true,
                      message: translate(
                        'fuelImport.required.supplier',
                        language
                      ),
                    },
                  ]}
                >
                  <Select
                    placeholder={translate('fuelImport.supplier', language)}
                    style={{ width: '100%' }}
                    loading={!isSuppliersLoaded}
                    disabled={!isSuppliersLoaded}
                  >
                    {suppliers.map((supplier) => (
                      <Select.Option key={supplier.id} value={supplier.id}>
                        {`${supplier.name} (${supplier.phone})`}
                      </Select.Option>
                    ))}
                  </Select>
                </StyledFormItem>
                <StyledFormItem
                  name="volume"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.volume', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.volume || 0}
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.reject(
                            new Error(
                              translate('fuelImport.invalid.volume', language)
                            )
                          )
                        } else {
                          const index = tanks
                            .map((tank) => tank.id)
                            .indexOf(getFieldValue('tankId'))
                          if (index > -1) {
                            const tank = tanks[index]
                            const emptyVolume = tank.volume - tank.remain
                            if (
                              value <= 0 ||
                              value > emptyVolume ||
                              emptyVolume < 0
                            ) {
                              return Promise.reject(
                                new Error(
                                  translate(
                                    'fuelImport.invalid.volume',
                                    language
                                  )
                                )
                              )
                            }
                          }
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    type="number"
                    placeholder={translate('fuelImport.volume', language)}
                    allowClear
                    onChange={() => calTotal()}
                  />
                </StyledFormItem>
                <StyledFormItem
                  name="unitPrice"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.unitPrice', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.unitPrice}
                  rules={[
                    {
                      required: true,
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value || value <= 0) {
                          return Promise.reject(
                            new Error(
                              translate(
                                'fuelImport.invalid.unitPrice',
                                language
                              )
                            )
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    type="number"
                    placeholder={translate('fuelImport.unitPrice', language)}
                    allowClear
                    onChange={() => calTotal()}
                  />
                </StyledFormItem>
                <StyledFormItem
                  name="vatPercent"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.vat', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.vatPercent || 0}
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (value && value < 0 && value > 100) {
                          return Promise.reject(
                            translate('fuelImport.invalid.vat', language)
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    type="number"
                    placeholder={translate('fuelImport.vat', language)}
                    onChange={() => calTotal()}
                  />
                </StyledFormItem>
              </>
            )}
            {!readOnlyMode && (
              <StyledFormItem
                name="note"
                label={
                  <StyledLabel>
                    {translate('fuelImport.note', language)}
                  </StyledLabel>
                }
                initialValue={selectedFuelImport?.note}
              >
                <Input
                  placeholder={translate('fuelImport.note', language)}
                  allowClear
                />
              </StyledFormItem>
            )}
            {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
              <>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.volume', language)}
                    </StyledLabel>
                  }
                >
                  <NumberFormat
                    suffixKey="common.unitType"
                    value={selectedFuelImport?.volume || 0}
                  />
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.unitPrice', language)}
                    </StyledLabel>
                  }
                >
                  <NumberFormat
                    suffixKey="common.moneyType"
                    value={selectedFuelImport?.unitPrice || 0}
                  />
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.vat', language)}
                    </StyledLabel>
                  }
                >
                  {selectedFuelImport?.vatPercent}
                </StyledFormItem>
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.amountPaid', language)}
                    </StyledLabel>
                  }
                >
                  <NumberFormat
                    suffixKey="common.moneyType"
                    value={selectedFuelImport?.amountPaid || 0}
                  />
                </StyledFormItem>
              </>
            )}
            {management.action === ActionEnum.CREATE && (
              <>
                <StyledFormItem
                  name="amountPaid"
                  label={
                    <StyledLabel>
                      {translate('fuelImport.amountPaid', language)}
                    </StyledLabel>
                  }
                  initialValue={selectedFuelImport?.amountPaid}
                  rules={[
                    {
                      message: '',
                    },
                    () => ({
                      validator(_, value) {
                        if (value && (value < 0 || value > total)) {
                          return Promise.reject(
                            translate('fuelImport.invalid.amountPaid', language)
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input
                    type="number"
                    placeholder={translate('fuelImport.amountPaid', language)}
                  />
                </StyledFormItem>
              </>
            )}
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('fuelImport.totalAmount', language)}
                </StyledLabel>
              }
            >
              <NumberFormat suffixKey="common.moneyType" value={total} />
            </StyledFormItem>
            {management.action === ActionEnum.VIEW &&
              selectedFuelImport &&
              selectedFuelImport.amountPaid <
                selectedFuelImport.volume * selectedFuelImport.unitPrice && (
                <StyledFormItem
                  label={
                    <StyledLabel>
                      {translate('fuelImport.accountsPayable', language)}
                    </StyledLabel>
                  }
                >
                  <NumberFormat
                    suffixKey="common.moneyType"
                    value={
                      selectedFuelImport.unitPrice * selectedFuelImport.volume -
                      selectedFuelImport.amountPaid
                    }
                  />
                </StyledFormItem>
              )}
          </>
        )}
        <ManageModalFooterComponent
          hasDefaultButtons={!isPaying}
          customButtons={
            <>
              {selectedFuelImport &&
                selectedFuelImport.amountPaid <
                  selectedFuelImport.volume * selectedFuelImport.unitPrice &&
                management &&
                management.action === ActionEnum.VIEW &&
                (!management.alertMessage ||
                  management.alertMessage.type !==
                    AlertMessageEnum.SUCCESS) && (
                  <Popconfirm
                    title={translate('fuelImport.pay.confirm', language)}
                    okText={translate('common.yes', language)}
                    cancelText={translate('common.no', language)}
                    placement="topRight"
                    onConfirm={pay}
                  >
                    <Button
                      htmlType="submit"
                      type="primary"
                      ghost
                      loading={management.actionProcessing}
                    >
                      {translate(
                        `fuelImport.${
                          management.actionProcessing ? 'paying' : 'pay'
                        }`,
                        language
                      )}
                    </Button>
                  </Popconfirm>
                )}
            </>
          }
          onDeleteConfirm={() => form.submit()}
        />
      </Form>
    </>
  )

  return <ManageModalComponent body={body} />
}
